import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { fetchCategories, submitCoupon } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const KINDS = ["code", "deal", "printable", "in-store"];

export default function Submit() {
  const { user, startGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    store: "",
    store_url: "",
    category: "",
    code: "",
    discount_label: "",
    discount_value: "",
    kind: "code",
    expires_at: "",
    terms: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCategories().then(setCats).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      startGoogleLogin();
      return;
    }
    setSubmitting(true);
    try {
      await submitCoupon({
        ...form,
        discount_value: form.discount_value ? parseFloat(form.discount_value) : null,
        code: form.code || null,
        terms: form.terms || null,
        expires_at: form.expires_at || null,
      });
      toast.success("Submitted! Pending review.", { duration: 3000 });
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-[900px] mx-auto px-6 md:px-10 py-16" data-testid="submit-signed-out">
        <div className="border-2 border-dashed border-black p-10 bg-white hard-shadow">
          <h1 className="serif text-5xl leading-tight tracking-tight mb-3">
            Share a deal.
          </h1>
          <p className="text-sm text-[#3a3a38] mb-6">
            Sign in to submit a coupon or in-store deal. Your submissions are
            reviewed before publishing.
          </p>
          <Button
            onClick={startGoogleLogin}
            data-testid="submit-signin-btn"
            className="rounded-none bg-[#D34836] hover:bg-[#b73824] text-white tracking-[0.2em] hard-shadow"
          >
            SIGN IN TO SUBMIT
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-10 py-10" data-testid="submit-page">
      <section className="border-b-2 border-black pb-4 mb-8">
        <div className="text-[10px] tracking-[0.3em] text-[#5c5c5c] mb-1">
          COMMUNITY DESK
        </div>
        <h1 className="serif text-5xl md:text-6xl leading-tight tracking-tight">
          Submit a deal.
        </h1>
      </section>

      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="submit-form">
        <Field label="TITLE" required>
          <Input
            required
            value={form.title}
            onChange={update("title")}
            placeholder="30% Off Sitewide"
            data-testid="field-title"
            className="rounded-none border-2 border-black bg-white h-11 mono focus-visible:ring-0 focus-visible:border-[#D34836]"
          />
        </Field>

        <Field label="STORE" required>
          <Input
            required
            value={form.store}
            onChange={update("store")}
            placeholder="Gap"
            data-testid="field-store"
            className="rounded-none border-2 border-black bg-white h-11 mono focus-visible:ring-0 focus-visible:border-[#D34836]"
          />
        </Field>

        <Field label="STORE URL" required>
          <Input
            required
            type="url"
            value={form.store_url}
            onChange={update("store_url")}
            placeholder="https://www.gap.com"
            data-testid="field-store-url"
            className="rounded-none border-2 border-black bg-white h-11 mono focus-visible:ring-0 focus-visible:border-[#D34836]"
          />
        </Field>

        <Field label="CATEGORY" required>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="rounded-none border-2 border-black bg-white h-11 mono focus:ring-0" data-testid="field-category">
              <SelectValue placeholder="Choose category" />
            </SelectTrigger>
            <SelectContent className="rounded-none border-2 border-black">
              {cats.map((c) => (
                <SelectItem key={c.name} value={c.name} className="rounded-none">
                  {c.name}
                </SelectItem>
              ))}
              {["Electronics","Fashion","Grocery","Food","Home","Travel","Beauty","Online","Health","Pets"].filter(n => !cats.find(c => c.name === n)).map((c) => (
                <SelectItem key={c} value={c} className="rounded-none">
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="TYPE" required>
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger className="rounded-none border-2 border-black bg-white h-11 mono focus:ring-0" data-testid="field-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none border-2 border-black">
              {KINDS.map((k) => (
                <SelectItem key={k} value={k} className="rounded-none">
                  {k.toUpperCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="DISCOUNT LABEL" required>
          <Input
            required
            value={form.discount_label}
            onChange={update("discount_label")}
            placeholder="30% OFF"
            data-testid="field-discount-label"
            className="rounded-none border-2 border-black bg-white h-11 mono focus-visible:ring-0 focus-visible:border-[#D34836]"
          />
        </Field>

        <Field label="PROMO CODE (optional)">
          <Input
            value={form.code}
            onChange={update("code")}
            placeholder="SAVE30"
            data-testid="field-code"
            className="rounded-none border-2 border-black bg-white h-11 mono focus-visible:ring-0 focus-visible:border-[#D34836]"
          />
        </Field>

        <Field label="EXPIRES">
          <Input
            type="date"
            value={form.expires_at}
            onChange={update("expires_at")}
            data-testid="field-expires"
            className="rounded-none border-2 border-black bg-white h-11 mono focus-visible:ring-0 focus-visible:border-[#D34836]"
          />
        </Field>

        <div className="md:col-span-2">
          <Field label="DESCRIPTION" required>
            <Textarea
              required
              value={form.description}
              onChange={update("description")}
              placeholder="Everything about the deal, exclusions, and how it works."
              data-testid="field-description"
              className="rounded-none border-2 border-black bg-white mono focus-visible:ring-0 focus-visible:border-[#D34836] min-h-[100px]"
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field label="TERMS (optional)">
            <Textarea
              value={form.terms}
              onChange={update("terms")}
              placeholder="One-time use, US only, excludes clearance..."
              data-testid="field-terms"
              className="rounded-none border-2 border-black bg-white mono focus-visible:ring-0 focus-visible:border-[#D34836] min-h-[70px]"
            />
          </Field>
        </div>

        <div className="md:col-span-2 flex items-center gap-3 pt-2">
          <Button
            type="submit"
            disabled={submitting}
            data-testid="submit-form-btn"
            className="rounded-none bg-[#D34836] hover:bg-[#b73824] text-white tracking-[0.25em] px-6 py-6 hard-shadow disabled:opacity-60"
          >
            {submitting ? "SUBMITTING..." : "SUBMIT FOR REVIEW"}
          </Button>
          <p className="text-[10px] tracking-[0.2em] text-[#5c5c5c]">
            SUBMISSIONS ARE PUBLISHED AFTER VERIFICATION
          </p>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.25em] text-[#5c5c5c] mb-1">
        {label} {required && <span className="text-[#D34836]">*</span>}
      </span>
      {children}
    </label>
  );
}
